import React from 'react';

import { formatDecimal } from '@/lib/utils';

import { TableBasicInput } from './table-basic-input';

export interface TablePercentageInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  hasError?: boolean;
  clampRange?: {
    min: number;
    max: number;
  }; // For chooseDiscount: 0-100
}

/**
 * Specialized percentage input for table inline editing
 * Handles percentage formatting, validation, and optional clamping
 */
export const TablePercentageInput: React.FC<TablePercentageInputProps> = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  disabled,
  autoFocus,
  className,
  hasError = false,
  clampRange,
}) => {
  return (
    <div className="relative flex h-full w-full items-center">
      <TableBasicInput
        value={
          value !== null && value !== undefined ? formatDecimal(value) : ''
        }
        onChange={(strValue) => {
          if (strValue === null) {
            onChange(null);
          } else {
            const val = strValue.replace(',', '.');
            let parsed = isNaN(parseFloat(val)) ? null : parseFloat(val);

            // Apply clamping if specified
            if (parsed !== null && clampRange) {
              if (parsed > clampRange.max) parsed = clampRange.max;
              if (parsed < clampRange.min) parsed = clampRange.min;
            }

            if (parsed !== value) {
              onChange(parsed);
            }
          }
        }}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        className={className}
        hasError={hasError}
        align="right"
        onInputChange={(e) => {
          // Allow only numbers, comma, and dot
          if (
            /[^0-9.,]/.test(e.target.value) &&
            e.target.value !== ',' &&
            e.target.value !== '.'
          ) {
            e.target.value = e.target.value.replace(/[^0-9.,]/, '');
          }
        }}
      />
      <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 select-none text-base text-gray-500">
        %
      </span>
    </div>
  );
};
